// Framework-free ref-counted MQTT subscription registry.
//
// Why a class outside React:
//   - The ref-count / dispatch / resubscribe logic is pure state machine
//     work — keeping it out of hooks lets us unit-test it with a fake client,
//     no jsdom, no act(), no testing-library.
//   - The React shell (context/MqttContext.tsx) owns the mqtt.connect
//     lifecycle and forwards events into this class.
//
// Ref-counting contract:
//   - First subscriber for a pattern → broker SUBSCRIBE is sent.
//   - Subsequent subscribers for the same pattern → no extra SUBSCRIBE.
//   - Last unsubscribe for a pattern → broker UNSUBSCRIBE is sent.
//   - On (re)connect, resubscribeAll() replays every active pattern.

import { matchesMqttPattern } from './mqttTopic';

/** The slice of MQTT packet metadata handlers actually use. Currently just
 *  `retain` so consumers can ignore the broker's stored last-known value
 *  (which is delivered immediately on subscribe and otherwise looks
 *  indistinguishable from a fresh publish). Real mqtt.IPublishPacket has
 *  many more fields — we don't need them. */
export type MqttPacketInfo = {
  retain?: boolean;
};

export type MqttMessageHandler = (
  topic: string,
  payload: Buffer,
  packet?: MqttPacketInfo,
) => void;

/** Minimal client surface the subscriber actually uses. Real mqtt.MqttClient
 *  satisfies this; tests pass a fake. */
export interface MqttClientLike {
  subscribe(topic: string): void;
  unsubscribe(topic: string): void;
}

export class MqttSubscriber {
  private subs = new Map<string, Set<MqttMessageHandler>>();
  private client: MqttClientLike | null = null;

  /** Attach (or detach) the underlying client. Pass null when the connection
   *  is torn down — subscriptions stay registered so a fresh client can
   *  resubscribeAll() on its next connect. */
  setClient(client: MqttClientLike | null): void {
    this.client = client;
  }

  /** Pattern strings currently subscribed to. Useful for tests and for the
   *  resubscribeAll() implementation. */
  patterns(): string[] {
    return Array.from(this.subs.keys());
  }

  /** Register a handler for a topic pattern. Returns an unsubscribe fn.
   *  Calling the returned fn more than once is safe (idempotent). */
  subscribe(pattern: string, handler: MqttMessageHandler): () => void {
    let set = this.subs.get(pattern);
    if (!set) {
      set = new Set();
      this.subs.set(pattern, set);
      // Tell the broker about the new pattern — but only on 0→1.
      this.client?.subscribe(pattern);
    }
    set.add(handler);

    let removed = false;
    return () => {
      if (removed) return;
      removed = true;
      const current = this.subs.get(pattern);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) {
        this.subs.delete(pattern);
        this.client?.unsubscribe(pattern);
      }
    };
  }

  /** Deliver an incoming message to every handler whose pattern matches.
   *  Handlers are isolated from one another — an exception in one does not
   *  stop the others from running. `packet` carries the MQTT metadata
   *  (`retain` etc.); handlers that care can opt-in to filtering. */
  dispatch(topic: string, payload: Buffer, packet?: MqttPacketInfo): void {
    for (const [pattern, handlers] of this.subs) {
      if (!matchesMqttPattern(pattern, topic)) continue;
      for (const handler of handlers) {
        try {
          handler(topic, payload, packet);
        } catch (e) {
          // Swallow + log: one buggy subscriber shouldn't poison the rest.
          // eslint-disable-next-line no-console
          console.error('MQTT subscriber handler threw:', e);
        }
      }
    }
  }

  /** Replay every active pattern to the current client. Call this after the
   *  client emits 'connect' or 'reconnect' — MQTT brokers do not retain
   *  subscriptions across a disconnected session by default. */
  resubscribeAll(): void {
    if (!this.client) return;
    for (const pattern of this.subs.keys()) {
      this.client.subscribe(pattern);
    }
  }
}
